import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useServicesSalonListQuery, useCategoriesListQuery } from '../../../../services/GLOWClient';
import { extractErrorMessage } from '../../../../utils/errorHandler';
import { PrimaryButton, TitleScreen } from '../../../../SystemDesign/styles';
import { 
  Button, 
  Typography, 
  Grid, 
  Card, 
  CardContent, 
  Box, 
  CircularProgress, 
  Chip, 
  IconButton, 
  Tooltip,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  Badge,
  Paper,
  Stack
} from '@mui/material';
import { 
  AccessTime, 
  CurrencyExchange, 
  Edit as EditIcon, 
  Delete as DeleteIcon,
  ArrowBackIos,
  ArrowForwardIos,
  Image as ImageIcon,
  Store as StoreIcon,
  Category as CategoryIcon,
  ViewModule as ViewModuleIcon,
  Translate as TranslateIcon
} from '@mui/icons-material';
import TranslationValidationModal from '../TranslationValidationModal';
import theme from '../../../../theme';
import { formatCurrency, formatDuration } from '../../../../utils/formatters';
import { BASE_URL } from '../../../../config';
import AddService from './addService';
import EditService from './editService';
import DeleteService from './deleteService';

const Service = ({ salonId, onSuccess }) => {
  const { t, i18n } = useTranslation();
  const [isAddServiceDialogOpen, setIsAddServiceDialogOpen] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [isUpdateDialogOpen, setIsUpdateDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [servicesByCategory, setServicesByCategory] = useState({});
  const [imageViewerOpen, setImageViewerOpen] = useState(false);
  const [selectedServiceImages, setSelectedServiceImages] = useState([]);
  const [selectedServiceName, setSelectedServiceName] = useState('');

  // Translation modal states
  const [translationModalOpen, setTranslationModalOpen] = useState(false);
  const [translationModalLoading, setTranslationModalLoading] = useState(false);
  const [translationService, setTranslationService] = useState(null);
  const [serviceTranslations, setServiceTranslations] = useState(null);
  const [selectedTab, setSelectedTab] = useState(0);
  const [categoryTabs, setCategoryTabs] = useState([]);
  
  // Notification state
  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'info'
  });
  
  const currentLanguage = (i18n.language || 'fr').split('-')[0].toLowerCase();
  
  // Fetch services data from the backend using the API hook for specific salon
  const { data: servicesData, error, isLoading, refetch } = useServicesSalonListQuery(
    salonId ? { salonId, language: i18n.language } : undefined,
    { skip: !salonId }
  );
  
  // Fetch categories for organizing services
  const { data: categoriesData, isLoading: categoriesLoading, refetch: refetchCategories } = useCategoriesListQuery({ language: i18n.language });

  const getTranslatedField = React.useCallback((item, field) => {
    const translationsKey = `${field}_translations`;
    const localizedKey = `localized_${field}`;
    const translations = item?.[translationsKey] || {};

    if (translations[currentLanguage]) {
      return translations[currentLanguage];
    }

    if (item?.[localizedKey]) {
      return item[localizedKey];
    }

    for (const fallbackLanguage of ['fr', 'en', 'ar']) {
      if (translations[fallbackLanguage]) {
        return translations[fallbackLanguage];
      }
    }

    return item?.[field] || '';
  }, [currentLanguage]);

  const getServiceName = React.useCallback((service) => {
    return getTranslatedField(service, 'name');
  }, [getTranslatedField]);

  const getServiceDescription = React.useCallback((service) => {
    return getTranslatedField(service, 'description');
  }, [getTranslatedField]);

  const getCategoryName = React.useCallback((service) => {
    if (!service?.category_details) {
      return '';
    }
    return getTranslatedField(service.category_details, 'name');
  }, [getTranslatedField]);

  const getSalonName = React.useCallback((service) => {
    if (!service?.salon_details) {
      return '';
    }
    return getTranslatedField(service.salon_details, 'name');
  }, [getTranslatedField]);

  const handleDelete = (service) => {
    setSelectedService(service);
    setIsDeleteDialogOpen(true);
  };

  const handleEdit = (service) => {
    setSelectedService(service);
    setIsUpdateDialogOpen(true);
  };

  const handleOpenTranslations = async (service) => {
    setTranslationService(service);
    setTranslationModalOpen(true);
    setTranslationModalLoading(true);
    setServiceTranslations(null);
    try {
      const token = localStorage.getItem('access_token') || sessionStorage.getItem('access_token');
      const serviceId = service.service_id || service.id;
      const response = await fetch(`/api/services/${serviceId}/`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setServiceTranslations({
          name: data.name_translations || {},
          description: data.description_translations || {},
        });
      }
    } catch (e) {
      console.warn('[service translations] fetch error', e);
    } finally {
      setTranslationModalLoading(false);
    }
  };

  const handleSaveServiceTranslations = async (editedTranslations) => {
    setTranslationModalOpen(false);
    if (!translationService) return;
    try {
      const token = localStorage.getItem('access_token') || sessionStorage.getItem('access_token');
      const serviceId = translationService.service_id || translationService.id;
      await fetch(`/api/services/${serviceId}/`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          name_translations: editedTranslations.name,
          description_translations: editedTranslations.description,
          translations_auto: false,
        }),
      });
      refetch();
    } catch (e) {
      console.warn('[service translations] patch error', e);
    }
  };

  const handleViewImages = (service) => {
    if (service.images && service.images.length > 0) {
      setSelectedServiceImages(service.images);
      setSelectedServiceName(getServiceName(service));
      setImageViewerOpen(true);
    }
  };

  // Handle closing dialogs
  const handleCloseDialogs = () => {
    setIsAddServiceDialogOpen(false);
    setIsUpdateDialogOpen(false);
    setIsDeleteDialogOpen(false);
    setSelectedService(null);
    refetch();
  };
  
  // Handle closing the notification
  const handleCloseNotification = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setNotification({ ...notification, open: false });
  };
  
  // Handle service creation success
  const handleServiceCreated = (newService, error) => {
    if (newService) {
      setNotification({
        open: true,
        message: t('pro_dashboard.service.created_success', { name: getServiceName(newService) }),
        severity: 'success'
      });
      refetch();
    } else if (error) {
      console.error('Failed to create service:', error);
      setNotification({
        open: true,
        message: t('pro_dashboard.service.create_error', { error: extractErrorMessage(error) }),
        severity: 'error'
      });
    }
    setIsAddServiceDialogOpen(false);
  };
  
  // Handle service update success
  const handleServiceUpdated = (updatedService, error) => {
    if (updatedService) {
      setNotification({
        open: true,
        message: t('pro_dashboard.service.updated_success', { name: getServiceName(updatedService) }),
        severity: 'success'
      });
      refetch();
      refetchCategories(); // Also refetch categories in case they were updated
    } else if (error) {
      console.error('Failed to update service:', error);
      setNotification({
        open: true,
        message: t('pro_dashboard.service.update_error', { error: extractErrorMessage(error) }),
        severity: 'error'
      });
    } else {
      // This handles the case where category was updated but no service data returned
      refetch();
      refetchCategories();
    }
    setIsUpdateDialogOpen(false);
    setSelectedService(null);
  };
  
  // Handle service deletion success
  const handleServiceDeleted = (deletedService, error) => {
    if (deletedService) {
      console.log('Service deleted successfully, refreshing list...');
      setNotification({
        open: true,
        message: t('pro_dashboard.service.deleted_success', { name: getServiceName(deletedService) }),
        severity: 'success'
      });
      // Refresh the services list
      refetch();
    } else if (error) {
      console.error('Failed to delete service:', error);
      setNotification({
        open: true,
        message: t('pro_dashboard.service.delete_error', { error: extractErrorMessage(error) }),
        severity: 'error'
      });
    }
    setIsDeleteDialogOpen(false);
    setSelectedService(null);
  };

  // Transform the API response data and organize by category
  useEffect(() => {
    if (servicesData && categoriesData) {
      const servicesByCat = {};
      
      // Create "Catégorisé" category for services with a category
      servicesByCat[t('pro_dashboard.service.categorized')] = [];
      
      // Create "Non catégorisé" category for services without a category
      servicesByCat[t('pro_dashboard.service.uncategorized')] = [];
      
      // Assign services to categories
      servicesData.forEach(service => {
        if (service && (service.category_details?.category_id || service.category)) {
          servicesByCat[t('pro_dashboard.service.categorized')].push(service);
        } else {
          servicesByCat[t('pro_dashboard.service.uncategorized')].push(service);
        }
      });
      
      // Create tabs array with counts - only show tabs that have services
      const tabs = Object.entries(servicesByCat)
        .filter(([categoryName, services]) => services.length > 0)
        .map(([categoryName, services]) => ({
          label: categoryName,
          count: services.length,
          services: services
        }));
      
      setCategoryTabs(tabs);
      setServicesByCategory(servicesByCat);
      
      // Reset tab selection if current tab is invalid
      if (selectedTab >= tabs.length) {
        setSelectedTab(0);
      }
    }
  }, [servicesData, categoriesData, t]);
  
  // State to track current image index for each service
  const [currentImageIndices, setCurrentImageIndices] = useState({});
  
  // Function to navigate to next image for a service
  const nextImage = (serviceId) => {
    setCurrentImageIndices(prev => {
      const currentIndex = prev[serviceId] || 0;
      const service = Object.values(servicesByCategory)
        .flat()
        .find(s => (s.service_id ?? s.id) === serviceId);
      
      if (!service || !service.images || service.images.length <= 1) return prev;
      
      return {
        ...prev,
        [serviceId]: (currentIndex + 1) % service.images.length
      };
    });
  };
  
  // Function to navigate to previous image for a service
  const prevImage = (serviceId) => {
    setCurrentImageIndices(prev => {
      const currentIndex = prev[serviceId] || 0;
      const service = Object.values(servicesByCategory)
        .flat()
        .find(s => (s.service_id ?? s.id) === serviceId);
      
      if (!service || !service.images || service.images.length <= 1) return prev;
      
      return {
        ...prev,
        [serviceId]: (currentIndex - 1 + service.images.length) % service.images.length
      };
    });
  };
  
  // Function to render service image or letter placeholder
  const renderServiceImage = (service) => {
    const MEDIA_HEIGHT = 160;
    
    const buildImageUrl = (url) => {
      if (!url) return null;
      
      if (/^https?:\/\//i.test(url)) {
        if (url.match(/^http:\/\/localhost\//) && !url.match(/^http:\/\/localhost:[0-9]+\//)) {
          return url.replace('http://localhost/', 'http://localhost:8012/');
        }
        return url;
      }
      
      if (url.startsWith('/media/')) {
        const baseUrl = window.location.hostname === 'localhost' ? 
          'http://localhost:8012' : 
          BASE_URL.replace(/\/$/, '');
        return `${baseUrl}${url}`;
      }
      
      if (url.startsWith('media/')) {
        const baseUrl = window.location.hostname === 'localhost' ? 
          'http://localhost:8012' : 
          BASE_URL.replace(/\/$/, '');
        return `${baseUrl}/${url}`;
      }
      
      const baseUrl = window.location.hostname === 'localhost' ? 
        'http://localhost:8012' : 
        BASE_URL.replace(/\/$/, '');
      const cleanUrl = url.startsWith('/') ? url : `/${url}`;
      return `${baseUrl}${cleanUrl}`;
    };
    
    // Check for multiple images first
    if (service && service.images && Array.isArray(service.images) && service.images.length > 0) {
      const sid = service?.service_id ?? service?.id;
      const currentIndex = currentImageIndices[sid] || 0;
      const imageToUse = service.images[currentIndex] || {};
      const imageUrl = imageToUse.image || imageToUse.image_url;
      
      if (imageUrl) {
        const fullImageUrl = buildImageUrl(imageUrl);
        
        return (
          <Box sx={{ position: 'relative', height: MEDIA_HEIGHT, overflow: 'hidden', borderRadius: '12px' }}>
            <img
              src={fullImageUrl}
              alt={getServiceName(service)}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                transition: 'transform 0.3s ease'
              }}
            />
            
            {service.images.length > 1 && (
              <React.Fragment>
                <IconButton 
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    prevImage(sid);
                  }}
                  sx={{
                    position: 'absolute',
                    left: 8,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    backgroundColor: 'rgba(0, 0, 0, 0.6)',
                    color: 'white',
                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.8)',
                    },
                    width: 32,
                    height: 32
                  }}
                >
                  <ArrowBackIos sx={{ fontSize: 14 }} />
                </IconButton>
                
                <IconButton 
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    nextImage(sid);
                  }}
                  sx={{
                    position: 'absolute',
                    right: 8,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    backgroundColor: 'rgba(0, 0, 0, 0.6)',
                    color: 'white',
                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.8)',
                    },
                    width: 32,
                    height: 32
                  }}
                >
                  <ArrowForwardIos sx={{ fontSize: 14 }} />
                </IconButton>
                
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: 8,
                    right: 8,
                    backgroundColor: 'rgba(0, 0, 0, 0.7)',
                    color: 'white',
                    borderRadius: 2,
                    padding: '4px 8px',
                    fontSize: '12px',
                    fontWeight: 500
                  }}
                >
                  {currentIndex + 1}/{service.images.length}
                </Box>
              </React.Fragment>
            )}
          </Box>
        );
      }
    }
    
    // Fall back to legacy single image or placeholder
    const hasImage = service && (service.image || service.image_url) && 
                    (typeof service.image === 'string' || typeof service.image_url === 'string');
    
    const imageUrl = (() => {
      if (service.image && typeof service.image === 'string') {
        return buildImageUrl(service.image);
      } else if (service.image_url && typeof service.image_url === 'string') {
        return buildImageUrl(service.image_url);
      }
      return null;
    })();

    if (hasImage && imageUrl) {
      return (
        <Box sx={{ height: MEDIA_HEIGHT, overflow: 'hidden', borderRadius: '12px' }}>
          <img
            src={imageUrl}
            alt={getServiceName(service)}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover'
            }}
          />
        </Box>
      );
    } else {
      return (
        <Box
          sx={{
            height: MEDIA_HEIGHT,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: `linear-gradient(135deg, ${theme.colors.primary[100]}, ${theme.colors.secondary[100]})`,
            color: theme.colors.primary[600],
            borderRadius: '12px'
          }}
        >
          <Typography 
            variant="h1" 
            sx={{ 
              fontWeight: 'bold',
              fontSize: '3rem',
              textTransform: 'uppercase',
              userSelect: 'none',
              opacity: 0.8
            }}
          >
            {getServiceName(service) ? getServiceName(service).charAt(0) : 'S'}
          </Typography>
        </Box>
      );
    }
  };

  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
  };

  const getCurrentServices = () => {
    if (!categoryTabs || !categoryTabs[selectedTab]) return [];
    return categoryTabs[selectedTab].services || [];
  };

  return (
    <>
      <Box sx={{ 
        minHeight: '100vh',
        background: `linear-gradient(135deg, ${theme.colors.grey[50]} 0%, ${theme.colors.primary[25]} 100%)`,
        padding: '32px'
      }}>
        {/* Header Section */}
        {/* <Paper
          elevation={0}
          sx={{
            padding: '32px',
            marginBottom: '32px',
            background: 'linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0.7) 100%)',
            backdropFilter: 'blur(10px)',
            borderRadius: '24px',
            border: `1px solid ${theme.colors.border.light}`,
          }}
        >
          <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={3}>
            <Box>
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 800,
                  background: `linear-gradient(135deg, ${theme.colors.primary[600]}, ${theme.colors.secondary[600]})`,
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  marginBottom: '8px',
                  fontSize: { xs: '1.8rem', sm: '2.2rem', md: '2.5rem' }
                }}
              >
                Gestion des services
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: theme.colors.text.secondary,
                  fontSize: '1.1rem'
                }}
              >
                Gérez tous vos services par catégorie
              </Typography>
            </Box>
            
            <Button
              onClick={() => setIsAddServiceDialogOpen(true)}
              variant="contained"
              size="large"
              startIcon={<ViewModuleIcon />}
              sx={{
                background: `linear-gradient(135deg, ${theme.colors.secondary[500]}, ${theme.colors.secondary[600]})`,
                borderRadius: '16px',
                padding: '12px 32px',
                textTransform: 'none',
                fontSize: '16px',
                fontWeight: 600,
                boxShadow: `0 8px 32px ${theme.colors.secondary[500]}40`,
                '&:hover': {
                  background: `linear-gradient(135deg, ${theme.colors.secondary[600]}, ${theme.colors.secondary[700]})`,
                  transform: 'translateY(-2px)',
                  boxShadow: `0 12px 40px ${theme.colors.secondary[500]}50`,
                },
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              }}
            >
              Ajouter service
            </Button>
          </Stack>
        </Paper> */}

        {/* Content Section */}
        <Paper
          elevation={0}
          sx={{
            background: 'rgba(255,255,255,0.8)',
            backdropFilter: 'blur(10px)',
            borderRadius: '24px',
            border: `1px solid ${theme.colors.border.light}`,
            overflow: 'hidden'
          }}
        >
          {isLoading || categoriesLoading ? (
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '80px 0',
              color: theme.colors.text.secondary
            }}>
              <CircularProgress size={48} sx={{ marginBottom: '16px' }} />
              <Typography variant="h6" sx={{ opacity: 0.7 }}>
                {t('pro_dashboard.service.loading')}
              </Typography>
            </Box>
          ) : error ? (
            <Box sx={{ 
              padding: '40px',
              textAlign: 'center'
            }}>
              <Alert severity="error" sx={{ borderRadius: '12px' }}>
                <Typography variant="h6">
                  {t('pro_dashboard.service.load_error', { error: error?.message || t('common.unknown_error') })}
                </Typography>
              </Alert>
            </Box>
          ) : (!servicesData || servicesData.length === 0) ? (
            <Box sx={{ 
              padding: '80px 40px',
              textAlign: 'center'
            }}>
              <ViewModuleIcon sx={{ fontSize: 64, color: theme.colors.text.secondary, opacity: 0.5, marginBottom: '16px' }} />
              <Typography variant="h5" sx={{ marginBottom: '8px', color: theme.colors.text.primary }}>
                {t('pro_dashboard.service.no_services')}
              </Typography>
              <Typography variant="body1" sx={{ color: theme.colors.text.secondary }}>
                {t('pro_dashboard.service.add_first_service')}
              </Typography>
            </Box>
          ) : (
            <>
              {/* Category Tabs */}
              <Box sx={{ borderBottom: `1px solid ${theme.colors.border.light}` }}>
                <Tabs
                  value={selectedTab}
                  onChange={handleTabChange}
                  variant="scrollable"
                  scrollButtons="auto"
                  sx={{
                    padding: '0 24px',
                    '& .MuiTabs-indicator': {
                      height: 3,
                      borderRadius: '3px',
                      background: `linear-gradient(135deg, ${theme.colors.primary[500]}, ${theme.colors.secondary[500]})`,
                    },
                  }}
                >
                  {categoryTabs.map((category, index) => (
                    <Tab
                      key={index}
                      icon={
                        <Badge 
                          badgeContent={category.count} 
                          color="secondary"
                          sx={{
                            '& .MuiBadge-badge': {
                              backgroundColor: theme.colors.secondary[500],
                              color: 'white',
                              fontWeight: 600,
                              fontSize: '11px'
                            }
                          }}
                        >
                          {category.label === 'Tous' ? <ViewModuleIcon /> : <CategoryIcon />}
                        </Badge>
                      }
                      label={
                        <Typography sx={{ 
                          textTransform: 'none', 
                          fontWeight: selectedTab === index ? 600 : 500,
                          fontSize: '14px'
                        }}>
                          {category.label}
                        </Typography>
                      }
                      iconPosition="top"
                      sx={{
                        minWidth: 120,
                        padding: '16px 12px',
                        margin: '0 4px',
                        borderRadius: '12px 12px 0 0',
                        transition: 'all 0.3s ease',
                        '&.Mui-selected': {
                          backgroundColor: `${theme.colors.primary[50]}80`,
                          color: theme.colors.primary[700],
                        },
                        '&:hover': {
                          backgroundColor: `${theme.colors.primary[25]}`,
                        }
                      }}
                    />
                  ))}
                </Tabs>
              </Box>

              {/* Services Grid */}
              <Box sx={{ padding: { xs: 0, md: '32px' } }}>
                <Grid container spacing={{ xs: 0, md: 3 }}>
                  {getCurrentServices().map((service, idx) => service && (
                    <Grid item xs={12} sm={6} md={4} lg={3} key={`service-${service?.service_id ?? service?.id ?? idx}`}>
                      <Card
                        sx={{
                          width: '100%',
                          height: { xs: 'auto', md: '400px' },
                          display: 'flex',
                          flexDirection: 'column',
                          background: { xs: 'transparent', md: 'linear-gradient(135deg, #ffffff 0%, #fafafa 100%)' },
                          border: { xs: 'none', md: `1px solid ${theme.colors.border.light}` },
                          borderRadius: { xs: 0, md: '20px' },
                          transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                          position: 'relative',
                          overflow: 'hidden',
                          '&::before': {
                            content: '""',
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            height: { xs: 0, md: '4px' },
                            background: `linear-gradient(90deg, ${theme.colors.primary[400]}, ${theme.colors.secondary[400]})`,
                          },
                          '&:hover': {
                            transform: { xs: 'none', md: 'translateY(-8px) scale(1.02)' },
                            boxShadow: { xs: 'none', md: `0 20px 40px ${theme.colors.primary[500]}20` },
                            borderColor: { xs: 'transparent', md: theme.colors.primary[200] },
                            '& .service-image img': {
                              transform: { xs: 'none', md: 'scale(1.05)' },
                            },
                            '& .service-actions': {
                              opacity: 1,
                              transform: 'translateY(0)',
                            },
                          },
                        }}
                      >
                        {/* Service Image */}
                        <Box className="service-image" sx={{ overflow: 'hidden' }}>
                          {renderServiceImage(service)}
                        </Box>

                        <CardContent
                          sx={{
                            flexGrow: 1,
                            display: 'flex',
                            flexDirection: 'column',
                            padding: '20px',
                            gap: '12px',
                            '&:last-child': {
                              paddingBottom: '20px',
                            },
                          }}
                        >
                          {/* Service Header */}
                          <Box>
                            <Typography
                              variant="h6"
                              sx={{
                                fontWeight: 700,
                                color: theme.colors.primary[700],
                                fontSize: '18px',
                                lineHeight: '1.3',
                                marginBottom: '8px',
                                display: '-webkit-box',
                                WebkitLineClamp: 1,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                              }}
                            >
                              {getServiceName(service)}
                            </Typography>

                            {/* Chips */}
                            <Stack direction="row" spacing={1} sx={{ marginBottom: '12px' }}>
                              {getSalonName(service) && (
                                <Chip
                                  icon={<StoreIcon sx={{ fontSize: '14px !important' }} />}
                                  label={getSalonName(service)}
                                  size="small"
                                  sx={{
                                    background: `linear-gradient(135deg, ${theme.colors.primary[50]}, ${theme.colors.primary[100]})`,
                                    color: theme.colors.primary[700],
                                    fontWeight: 500,
                                    fontSize: '11px',
                                    height: '24px',
                                    maxWidth: '140px',
                                    '& .MuiChip-label': {
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap',
                                    }
                                  }}
                                />
                              )}
                              <Chip
                                label={getCategoryName(service) || t('pro_dashboard.service.uncategorized')}
                                size="small"
                                sx={{
                                  background: `linear-gradient(135deg, ${theme.colors.secondary[50]}, ${theme.colors.secondary[100]})`,
                                  color: theme.colors.secondary[700],
                                  fontWeight: 500,
                                  fontSize: '11px',
                                  height: '24px',
                                  maxWidth: '140px',
                                  '& .MuiChip-label': {
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                  }
                                }}
                              />
                            </Stack>
                          </Box>

                          {/* Service Details */}
                          <Stack spacing={1}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <CurrencyExchange 
                                fontSize="small" 
                                sx={{ 
                                  color: theme.colors.success[500], 
                                  fontSize: '16px',
                                  padding: '2px',
                                  backgroundColor: theme.colors.success[50],
                                  borderRadius: '4px'
                                }} 
                              />
                              <Typography 
                                variant="body2" 
                                sx={{ 
                                  color: theme.colors.text.primary, 
                                  fontSize: '14px',
                                  fontWeight: 600
                                }}
                              >
                                {service.formatted_price || formatCurrency(service.price || 0)}
                              </Typography>
                            </Box>

                            <Box>
                              <Chip
                                label={service.prix_fixe ? t('pro_dashboard.service.fixed_price') : t('pro_dashboard.service.variable_price')}
                                size="small"
                                sx={{
                                  height: '22px',
                                  fontWeight: 600,
                                  fontSize: '11px',
                                  backgroundColor: service.prix_fixe ? theme.colors.success[50] : theme.colors.warning[50],
                                  color: service.prix_fixe ? theme.colors.success[700] : theme.colors.warning[800],
                                }}
                              />
                            </Box>

                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <AccessTime 
                                fontSize="small" 
                                sx={{ 
                                  color: theme.colors.info[500], 
                                  fontSize: '16px',
                                  padding: '2px',
                                  backgroundColor: theme.colors.info[50],
                                  borderRadius: '4px'
                                }} 
                              />
                              <Typography 
                                variant="body2" 
                                sx={{ 
                                  color: theme.colors.text.primary, 
                                  fontSize: '14px',
                                  fontWeight: 500
                                }}
                              >
                                {service.formatted_duration || formatDuration(service.duration || 0)}
                              </Typography>
                            </Box>
                          </Stack>

                          {/* Description */}
                          <Box sx={{ flexGrow: 1 }}>
                            <Typography
                              variant="body2"
                              sx={{
                                color: theme.colors.text.secondary,
                                lineHeight: '1.4',
                                fontSize: '13px',
                                display: '-webkit-box',
                                WebkitBoxOrient: 'vertical',
                                WebkitLineClamp: 3,
                                overflow: 'hidden',
                                textAlign: 'justify',
                              }}
                            >
                              {getServiceDescription(service) || t('pro_dashboard.service.no_description')}
                            </Typography>
                          </Box>

                          {/* Action Buttons */}
                          <Box
                            className="service-actions"
                            sx={{
                              display: 'flex',
                              justifyContent: 'center',
                              gap: '8px',
                              marginTop: 'auto',
                              opacity: 1,
                              transform: 'none',
                              transition: 'all 0.3s ease',
                            }}
                          >
                            {service.images && service.images.length > 0 && (
                              <Tooltip title={t('pro_dashboard.service.view_images')} arrow>
                                <IconButton
                                  onClick={() => handleViewImages(service)}
                                  sx={{
                                    padding: '8px',
                                    borderRadius: '12px',
                                    backgroundColor: theme.colors.info[50],
                                    color: theme.colors.info[600],
                                    border: `1px solid ${theme.colors.info[200]}`,
                                    '&:hover': {
                                      backgroundColor: theme.colors.info[100],
                                      transform: 'scale(1.1)',
                                    },
                                    transition: 'all 0.2s ease',
                                  }}
                                >
                                  <ImageIcon sx={{ fontSize: '18px' }} />
                                </IconButton>
                              </Tooltip>
                            )}

                            <Tooltip title={t('pro_salon.translations.see_translations', 'Voir traductions')} arrow>
                              <Button
                                onClick={() => handleOpenTranslations(service)}
                                startIcon={<TranslateIcon sx={{ fontSize: '18px !important' }} />}
                                sx={{
                                  padding: '8px 12px',
                                  borderRadius: '12px',
                                  backgroundColor: theme.colors.secondary[50],
                                  color: theme.colors.secondary[700],
                                  border: `1px solid ${theme.colors.secondary[200]}`,
                                  fontSize: '12px',
                                  fontWeight: 600,
                                  textTransform: 'none',
                                  '&:hover': {
                                    backgroundColor: theme.colors.secondary[100],
                                    transform: 'translateY(-1px)',
                                  },
                                  transition: 'all 0.2s ease',
                                }}
                              >
                                {t('pro_salon.translations.see_translations', 'Voir traduction')}
                              </Button>
                            </Tooltip>

                            <Tooltip title={t('pro_dashboard.common.edit')} arrow>
                              <IconButton
                                onClick={() => handleEdit(service)}
                                sx={{
                                  padding: '8px',
                                  borderRadius: '12px',
                                  backgroundColor: theme.colors.primary[50],
                                  color: theme.colors.primary[600],
                                  border: `1px solid ${theme.colors.primary[200]}`,
                                  '&:hover': {
                                    backgroundColor: theme.colors.primary[100],
                                    transform: 'scale(1.1)',
                                  },
                                  transition: 'all 0.2s ease',
                                }}
                              >
                                <EditIcon sx={{ fontSize: '18px' }} />
                              </IconButton>
                            </Tooltip>

                            <Tooltip title={t('pro_dashboard.common.delete')} arrow>
                              <IconButton
                                onClick={() => handleDelete(service)}
                                sx={{
                                  padding: '8px',
                                  borderRadius: '12px',
                                  backgroundColor: theme.colors.error[50],
                                  color: theme.colors.error[500],
                                  border: `1px solid ${theme.colors.error[200]}`,
                                  '&:hover': {
                                    backgroundColor: theme.colors.error[100],
                                    transform: 'scale(1.1)',
                                  },
                                  transition: 'all 0.2s ease',
                                }}
                              >
                                <DeleteIcon sx={{ fontSize: '18px' }} />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>

                {/* Empty State for Categories */}
                {getCurrentServices().length === 0 && (
                  <Box sx={{ 
                    textAlign: 'center',
                    padding: '60px 20px',
                    color: theme.colors.text.secondary
                  }}>
                    <CategoryIcon sx={{ fontSize: 64, opacity: 0.3, marginBottom: '16px' }} />
                    <Typography variant="h6" sx={{ marginBottom: '8px' }}>
                      {t('pro_dashboard.service.no_services_in_category')}
                    </Typography>
                    <Typography variant="body2">
                      {t('pro_dashboard.service.services_will_appear_here')}
                    </Typography>
                  </Box>
                )}
              </Box>
            </>
          )}
        </Paper>
      </Box>

      {/* Dialog for adding a new service */}
      <AddService 
        open={isAddServiceDialogOpen} 
        onClose={() => setIsAddServiceDialogOpen(false)} 
        onSuccess={handleServiceCreated} 
      />
      
      {/* Dialogs for editing and deleting services */}
      <TranslationValidationModal
        open={translationModalOpen}
        onClose={() => setTranslationModalOpen(false)}
        onValidate={handleSaveServiceTranslations}
        salonName={translationService?.name || ''}
        salonDescription={translationService?.description || ''}
        isLoading={translationModalLoading}
        translations={serviceTranslations}
      />

      {selectedService && (
        <>
          <EditService 
            open={isUpdateDialogOpen} 
            onClose={() => setIsUpdateDialogOpen(false)} 
            onSuccess={handleServiceUpdated} 
            serviceData={selectedService} 
          />
          
          <DeleteService
            open={isDeleteDialogOpen}
            onClose={() => {
              setIsDeleteDialogOpen(false);
              setSelectedService(null);
            }}
            onSuccess={handleServiceDeleted}
            serviceId={selectedService?.id || selectedService?.service_id}
            serviceName={selectedService?.name}
            serviceData={selectedService}
          />
        </>
      )}
      
      {/* Enhanced Image Viewer Dialog */}
      <Dialog
        open={imageViewerOpen}
        onClose={() => setImageViewerOpen(false)}
        maxWidth="lg"
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            borderRadius: '24px',
            maxHeight: '90vh',
            background: 'linear-gradient(135deg, #ffffff 0%, #fafafa 100%)',
          }
        }}
      >
        <DialogTitle sx={{
          background: `linear-gradient(135deg, ${theme.colors.primary[50]} 0%, ${theme.colors.secondary[50]} 100%)`,
          color: theme.colors.primary[700],
          fontWeight: 700,
          borderBottom: `1px solid ${theme.colors.border.light}`,
          borderRadius: '24px 24px 0 0',
          padding: '24px 32px',
        }}>
          <Stack direction="row" alignItems="center" spacing={2}>
            <ImageIcon sx={{ fontSize: 28 }} />
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              {t('pro_dashboard.service.service_images', { name: selectedServiceName })}
            </Typography>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ padding: '32px' }}>
          <Grid container spacing={3}>
            {selectedServiceImages.map((image, index) => {
              const imageUrl = (() => {
                if (image.image && typeof image.image === 'string') {
                  return image.image;
                } else if (image.image_url && typeof image.image_url === 'string') {
                  return image.image_url;
                }
                return null;
              })();

              if (!imageUrl) return null;

              const buildImageUrl = (url) => {
                if (!url) return null;
                
                if (/^https?:\/\//i.test(url)) {
                  if (url.match(/^http:\/\/localhost\//) && !url.match(/^http:\/\/localhost:[0-9]+\//)) {
                    return url.replace('http://localhost/', 'http://localhost:8012/');
                  }
                  return url;
                }
                
                if (url.startsWith('/media/')) {
                  const baseUrl = window.location.hostname === 'localhost' ? 
                    'http://localhost:8012' : 
                    BASE_URL.replace(/\/$/, '');
                  return `${baseUrl}${url}`;
                }
                
                if (url.startsWith('media/')) {
                  const baseUrl = window.location.hostname === 'localhost' ? 
                    'http://localhost:8012' : 
                    BASE_URL.replace(/\/$/, '');
                  return `${baseUrl}/${url}`;
                }
                
                const baseUrl = window.location.hostname === 'localhost' ? 
                  'http://localhost:8012' : 
                  BASE_URL.replace(/\/$/, '');
                const cleanUrl = url.startsWith('/') ? url : `/${url}`;
                return `${baseUrl}${cleanUrl}`;
              };

              const fullImageUrl = buildImageUrl(imageUrl);

              return (
                <Grid item xs={12} sm={6} md={4} key={index}>
                  <Paper
                    elevation={2}
                    sx={{
                      borderRadius: '16px',
                      overflow: 'hidden',
                      transition: 'all 0.3s ease',
                      cursor: 'pointer',
                      '&:hover': {
                        transform: 'scale(1.05)',
                        boxShadow: `0 12px 24px ${theme.colors.primary[500]}20`,
                      }
                    }}
                  >
                    <img
                      src={fullImageUrl}
                      alt={`${selectedServiceName} - Image ${index + 1}`}
                      style={{
                        width: '100%',
                        height: '240px',
                        objectFit: 'cover',
                        display: 'block'
                      }}
                    />
                    <Box sx={{ 
                      padding: '12px',
                      backgroundColor: theme.colors.grey[50],
                      textAlign: 'center'
                    }}>
                      <Typography variant="body2" sx={{ 
                        color: theme.colors.text.secondary,
                        fontWeight: 500
                      }}>
                        {t('pro_dashboard.service.image_counter', { current: index + 1, total: selectedServiceImages.length })}
                      </Typography>
                    </Box>
                  </Paper>
                </Grid>
              );
            })}
          </Grid>
        </DialogContent>
        <DialogActions sx={{ 
          padding: '24px 32px',
          borderTop: `1px solid ${theme.colors.border.light}`,
          backgroundColor: theme.colors.grey[25],
          borderRadius: '0 0 24px 24px'
        }}>
          <Button 
            onClick={() => setImageViewerOpen(false)}
            variant="contained"
            sx={{
              borderRadius: '12px',
              textTransform: 'none',
              fontWeight: 600,
              padding: '12px 24px',
              background: `linear-gradient(135deg, ${theme.colors.primary[500]}, ${theme.colors.primary[600]})`,
              '&:hover': {
                background: `linear-gradient(135deg, ${theme.colors.primary[600]}, ${theme.colors.primary[700]})`,
              }
            }}
          >
            {t('pro_dashboard.common.close')}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Enhanced Notification Snackbar */}
      <Snackbar 
        open={notification.open} 
        autoHideDuration={4000} 
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        sx={{
          '& .MuiSnackbar-root': {
            top: '80px !important',
          }
        }}
      >
        <Alert 
          onClose={handleCloseNotification} 
          severity={notification.severity}
          variant="filled"
          sx={{ 
            minWidth: '300px',
            borderRadius: '16px',
            fontWeight: 600,
            fontSize: '0.95rem',
            boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
            '& .MuiAlert-icon': {
              fontSize: '28px',
              alignItems: 'center'
            },
            '& .MuiAlert-message': {
              padding: '8px 0',
              display: 'flex',
              alignItems: 'center'
            },
            '& .MuiAlert-action': {
              paddingTop: '4px'
            },
            ...(notification.severity === 'success' && {
              background: `linear-gradient(135deg, ${theme.colors.success[500]}, ${theme.colors.success[600]})`,
            }),
            ...(notification.severity === 'error' && {
              background: `linear-gradient(135deg, ${theme.colors.error[500]}, ${theme.colors.error[600]})`,
            }),
            ...(notification.severity === 'warning' && {
              background: `linear-gradient(135deg, ${theme.colors.warning[500]}, ${theme.colors.warning[600]})`,
            }),
            ...(notification.severity === 'info' && {
              background: `linear-gradient(135deg, ${theme.colors.info[500]}, ${theme.colors.info[600]})`,
            }),
          }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default Service;